// Render a field JSON (regular lat/lon grid, rows south-to-north) into RGBA
// pixels for an L.imageOverlay. Leaflet draws an image overlay linearly in
// Web Mercator, so each output row samples the grid at the latitude that row
// actually covers (inverse Mercator); a plain lat-row copy would visibly
// stretch tall boxes.

import { buildColorLUT } from "./colorscale.js";

const R = Math.PI / 180;
// Web Mercator's latitude limit: Leaflet clamps to this, so the image must too.
export const MAX_LAT = 85.0511287798;
export const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * R) / 2));
export const invMercY = (y) => (2 * Math.atan(Math.exp(y)) - Math.PI / 2) / R;

/** Alpha for a normalised value t in [0,1]. */
export function alphaFor(t, fade) {
  // Ramp in over the first ~15% of the range so faint fringes stay faint.
  if (fade === "low") return t <= 0.004 ? 0 : Math.min(1, t / 0.15);
  if (fade === "high") return t >= 0.996 ? 0 : Math.min(1, (1 - t) / 0.15);
  return 1;
}

/**
 * @param {{lat0:number, lon0:number, dlat:number, dlon:number, nj:number, ni:number, values:(number|null)[]}} field
 * @param {[number, number][]} stops  colorscale stops
 * @param {[number, number]} range    [lo, hi] display range
 * @param {{fade?: "low"|"high"|null, height?: number}} [opts]
 * @returns {{width:number, height:number, data:Uint8ClampedArray, bounds:[[number,number],[number,number]]}}
 */
export function renderField(field, stops, range, { fade = null, height } = {}) {
  const { lat0, lon0, dlat, dlon, nj, ni, values } = field;
  const lat1 = lat0 + dlat * (nj - 1);
  // Clamp to what Web Mercator can draw (a global grid reaches the poles).
  const latS = Math.max(lat0 - dlat / 2, -MAX_LAT), latN = Math.min(lat1 + dlat / 2, MAX_LAT);
  const width = ni;
  const h = height || nj;
  const lut = buildColorLUT(stops);
  const [lo, hi] = range;
  const span = hi - lo || 1;
  const data = new Uint8ClampedArray(width * h * 4);
  const yN = mercY(latN), yS = mercY(latS);
  for (let row = 0; row < h; row++) {
    // Output row 0 is the NORTH edge.
    const lat = invMercY(yN - ((row + 0.5) / h) * (yN - yS));
    const j = Math.min(nj - 1, Math.max(0, Math.round((lat - lat0) / dlat)));
    for (let i = 0; i < width; i++) {
      const v = values[j * ni + i];
      const o = (row * width + i) * 4;
      if (v == null || !Number.isFinite(v)) continue; // alpha 0
      const t = Math.min(1, Math.max(0, (v - lo) / span));
      const k = Math.round(t * 255) * 3;
      data[o] = lut[k]; data[o + 1] = lut[k + 1]; data[o + 2] = lut[k + 2];
      data[o + 3] = Math.round(255 * alphaFor(t, fade));
    }
  }
  const west = lon0 - dlon / 2;
  return { width, height: h, data, bounds: [[latS, west], [latN, west + dlon * ni]] };
}

/** Browser helper: pixels -> data URL (needs a DOM canvas). */
export function toDataUrl(img, doc = globalThis.document) {
  const c = doc.createElement("canvas");
  c.width = img.width;
  c.height = img.height;
  const ctx = c.getContext("2d");
  ctx.putImageData(new ImageData(img.data, img.width, img.height), 0, 0);
  return c.toDataURL();
}
